import React, { useEffect, useState } from "react";
import {
  Box,
  Flex,
  Text,
  Tabs,
  TabList,
  Tab,
} from "@chakra-ui/react";
import AddNewFAQButton from "../../components/faq/addnewfaq";
import { TableColumns } from "../../components/faq/tablecolumns";
import { DataTable } from "@root/components";
import HelpIcon from "../../assets/icons/helpicon";
import CustomIcon from "../../assets/icons/resourceicon";
import AddFaqModal from "../../components/faq/AddFaqModal";
import { useTranslation } from "react-i18next";
import SearchInput from "@root/components/common/SearchInput";

import { TabType } from "@root/constants/faq-tabs";
import ResourcesTab from "@root/components/customer-center/tabs/ResourcesTab";
import { useFaqContext } from "@root/context/faq-context";
import { CRUD_TYPE_ENUM, DELETE, EDIT } from "@root/constants";
import PendingQATab from "@root/components/customer-center/tabs/PendingQA";
import { ModalDelete } from "@root/components/modal-delete";
import FAQHeaderSection from "@root/components/faq/FaqHeaderSection";

// tabItems now use `id` from enum + translated `label`
const tabItems = (t: (key: string) => string) => [
  {
    id: TabType.PendingQnA,
    label: t("faq.pendingQuestions"),
    icon: <HelpIcon boxSize="6" />,
  },
  { id: TabType.FAQS, label: t("faq.faqTab"), icon: <HelpIcon boxSize="6" /> },
  { id: TabType.RESOURCES, label: t("faq.resourcesTab"), icon: <CustomIcon /> },
];

const FAQnQAPage: React.FC = () => {
  const { t } = useTranslation("common");

  // store stable enum ID, not label
  const [activeTab, setActiveTab] = useState<TabType>(TabType.FAQS);

  const {
    fetchFaqs,
    pageNo,
    pageSize,
    faqData,
    pageCount,
    removeFaq,
    setPageNo,
    search,
    setSearch,
  } = useFaqContext();

  const [selectedFaq, setSelectedFaq] = useState<FAQ>({} as FAQ);
  const [isfaqModalOpen, setIsfaqModalOpen] = useState(false);
  const [action, setAction] = useState(CRUD_TYPE_ENUM.DEFAULT);
  const [isDeleteModal, setIsDeleteModal] = useState(false);
  const [faqToDelete, setFaqToDelete] = useState<FAQ | null>(null);

  const payload = {
    offset: (pageNo - 1) * pageSize,
    limit: pageSize,
    search: search,
  };

  const handleActions = (action: string, faq?: FAQ) => {
    if (!faq) return;

    if (action === DELETE) {
      setFaqToDelete(faq);
      setIsDeleteModal(true);
    }

    if (action === EDIT) {
      setAction(CRUD_TYPE_ENUM.UPDATE);
      setSelectedFaq(faq);
      setIsfaqModalOpen(true);
    }
  };

  const handleDeleteConfirm = () => {
    if (faqToDelete) {
      removeFaq(faqToDelete.id);
      setFaqToDelete(null);
      setIsDeleteModal(false);
    }
  };

  const handleDeleteCancel = () => {
    setFaqToDelete(null);
    setIsDeleteModal(false);
  };

  const handlePageChange = (newPage: number) => {
    setPageNo(newPage);
    const payload = {
      offset: (newPage - 1) * pageSize,
      limit: pageSize,
      search: search,
    };
    fetchFaqs(payload);
  };

  const handleOpen = () => {
    setAction(CRUD_TYPE_ENUM.ADD);
    setSelectedFaq({} as FAQ);
    setIsfaqModalOpen(true);
  };

  const handleClosing = () => {
    setSelectedFaq({} as FAQ);
  };

  useEffect(() => {
    fetchFaqs(payload);
  }, []);

  useEffect(() => {
    setPageNo(1);
    const searchPayload = {
      offset: 0,
      limit: pageSize,
      search: search,
    };
    fetchFaqs(searchPayload);
  }, [search]);

  const tabs = tabItems(t);

  return (
    <Box mx="auto" pb={4} px={4} m={5}>
      <FAQHeaderSection
        title={
          activeTab === TabType.FAQS
            ? t("faq.faqManagementTitle")
            : t("faq.ResourceManagementTitle")
        }
        subtitle={t("faq.subtitle")}
      />

      <Tabs
        mt={4}
        variant="unstyled"
        isFitted
        w="100%"
        index={tabs.findIndex((tab) => tab.id === activeTab)}
        onChange={(idx) => setActiveTab(tabs[idx].id)}
      >
        <TabList
          mb={4}
          w="100%"
          background="linear-gradient(0deg, rgba(205, 214, 220, 0.32), rgba(205, 214, 220, 0.32))"
          h={"51px"}
          borderRadius="10px"
          p={1}
          display="flex"
          boxShadow="sm"
          overflowX="auto"
          whiteSpace="nowrap"
        >
          {tabs?.map((tab) => (
            <Tab
              key={tab?.id}
              fontWeight="600"
              fontSize="18px"
              color="gray.700"
              _selected={{
                color: "#1819B4",
                bg: "white",
                border: "0.5px solid #A7B2BA",
                boxShadow: "0px 1px 4px 0px #0000001F",
                borderRadius: "10px",
              }}
              _focus={{ boxShadow: "none" }}
              py={4}
              px={6}
              display="flex"
              alignItems="center"
              justifyContent="center"
              gap={2}
              textColor={"#2F2F34"}
              transition="all 0.2s"
              borderRadius="10px"
              minW="180px"
            >
              <Box display="flex" alignItems="center" gap={2} fontWeight="bold">
                {tab?.icon}
                {tab?.label}
              </Box>
            </Tab>
          ))}
        </TabList>
      </Tabs>

      {activeTab === TabType.FAQS && (
        <>
          <Flex justify="flex-end" m={4}>
            <AddNewFAQButton
              onClick={handleOpen}
              label={t("faq.addNewFaqButton")}
            />
          </Flex>
          <AddFaqModal
            isOpen={isfaqModalOpen}
            onClose={() => setIsfaqModalOpen(false)}
            selectedFaq={selectedFaq}
            handleClosing={handleClosing}
            action={action}
          />

          <Flex justify="space-between" align="center" mb={4}>
            <Text fontWeight="bold" fontSize="lg">
              {t("faq.faqListHeading")}
            </Text>
          </Flex>

          <Text fontSize="sm" color="gray.500" mb={2}>
            {t("faq.faqListDescription")}
          </Text>
<Box   mb={4}>
          <SearchInput
            maxW="350px"
          
            placeholder={t("faq.searchPlaceholder")}
            value={search}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setSearch(e.target.value ?? "")
            }
          />
</Box>
          <DataTable
            columns={TableColumns(handleActions, t)}
            data={faqData ?? []}
            showPagination={true}
            page={pageNo}
            pageCount={pageCount}
            pageSize={pageSize}
            handlePageChange={handlePageChange}
            theadBg="#dad5f5"
            rowHoverBg="gray.50"
          />
          <ModalDelete
            isOpen={isDeleteModal}
            onClose={handleDeleteCancel}
            onConfirm={handleDeleteConfirm}
            title={t("faq.confirmDelete")}
            description={t("faq.confirmDeleteDescription")}
            deleteButtonTitle={t("faq.delete")}
            cancelButtonTitle={t("faq.cancel")}
          />
        </>
      )}

      {activeTab === TabType.RESOURCES && <ResourcesTab />}
      {activeTab === TabType.PendingQnA && <PendingQATab />}
    </Box>
  );
};

export default FAQnQAPage;
